import { axiosBuzzOne } from '../../../axios';

// School member management. Contract: buzz-one-backend
// src/modules/akademy/schools/API.md. BCC reaches only list / add / edit / removal —
// bulk add, the photo import and the classroom picker are a 403 for us.

const membersUrl = (schoolId) => `/akademy/schools/${schoolId}/members`;

/** Nest error bodies carry `message` as a string, or an array for validation errors. */
export const apiErrorMessage = (error, fallback = 'Something went wrong') => {
  const body = (error && error.response && error.response.data) || {};
  if (Array.isArray(body.message)) return body.message.join(', ');
  return body.message || (error && error.message) || fallback;
};

const isNotFound = (error) =>
  !!(error && error.response && error.response.status === 404);

export const getSchoolSubscriptions = async (schoolId) => {
  const response = await axiosBuzzOne({
    method: 'GET',
    url: '/buzz/subscriptions',
    params: { schoolId, ecosystem: 'AKADEMY' },
  });
  // Unlike the member routes, this one is not wrapped in `{ message, data }`.
  return (response.data && response.data.subscriptions) || [];
};

/** → { items: MemberRow[], total, page, limit } */
export const getSchoolMembers = async (schoolId, params) => {
  const response = await axiosBuzzOne({
    method: 'GET',
    url: membersUrl(schoolId),
    params,
  });
  return response.data.data;
};

export const addSchoolMember = async (schoolId, payload) => {
  const response = await axiosBuzzOne({
    method: 'POST',
    url: membersUrl(schoolId),
    data: payload,
  });
  return response.data;
};

/**
 * The `users/:userId` routes exist only where `user_management.school_membership` is on,
 * and a flag that is off is a 404. Elsewhere a member is keyed on a profile, so fall back
 * to the first profile they hold at the school — removal takes every profile either way.
 */
const withMemberKey = async (member, byUser, byProfile) => {
  try {
    return await byUser(member.user._id);
  } catch (error) {
    const profile = (member.profiles || [])[0];
    const profileId = profile && profile.profileId;
    if (!isNotFound(error) || !profileId) throw error;
    return byProfile(profileId);
  }
};

export const updateSchoolMember = (schoolId, member, data) =>
  withMemberKey(
    member,
    (userId) =>
      axiosBuzzOne({
        method: 'PATCH',
        url: `${membersUrl(schoolId)}/users/${userId}`,
        data,
      }).then((res) => res.data),
    (profileId) =>
      axiosBuzzOne({
        method: 'PATCH',
        url: `${membersUrl(schoolId)}/${profileId}`,
        data,
      }).then((res) => res.data)
  );

/** Resolves to `{ preview, remove }`: `remove()` deletes through the same key the preview used. */
export const getMemberRemoval = (schoolId, member) =>
  withMemberKey(
    member,
    async (userId) => {
      const url = `${membersUrl(schoolId)}/users/${userId}`;
      const res = await axiosBuzzOne({
        method: 'GET',
        url: `${url}/removal-preview`,
      });
      return { preview: res.data.data, url };
    },
    async (profileId) => {
      const url = `${membersUrl(schoolId)}/${profileId}`;
      const res = await axiosBuzzOne({
        method: 'GET',
        url: `${url}/removal-preview`,
      });
      return { preview: res.data.data, url };
    }
  ).then(({ preview, url }) => ({
    preview,
    remove: () =>
      axiosBuzzOne({ method: 'DELETE', url }).then((res) => res.data),
  }));
